import React from 'react';

import Error from '@gasbi/shared/web/components/ErrorBoundary/ErrorView';
import { Route, RouteObject, createBrowserRouter } from 'react-router-dom';

import { UserPermissions } from '@/features/permissions';
import type { UserPermissionType } from '@/features/permissions';
import Pages from '@/pages';

import RequireRoutePermission from '@components/RequireRoutePermission';

import { NotFound } from '@gasbi/shared';
import AuthCallback from './pages/auth/AuthCallback';

const Router = React.lazy(() => import('@/pages/Router'));
const Home = React.lazy(() => import('@/pages/Home'));
const CabinetUsers = React.lazy(() => import('@/pages/CabinetUsers'));
const MyCompany = React.lazy(() => import('@/pages/MyCompany'));
const Contracts = React.lazy(() => import('@/pages/Contracts'));
const ContractDetails = React.lazy(() => import('@/pages/ContractDetails'));
const ContractActs = React.lazy(
  () => import('@/pages/ContractDetails/views/Acts'),
);
const ContractPlannedVolumes = React.lazy(
  () => import('@/pages/ContractDetails/views/PlannedVolumes'),
);
const MeteringPoints = React.lazy(() => import('@/pages/MeteringPoints'));
const Consumption = React.lazy(() => import('@/pages/Consumption'));
const DocumentsAndActs = React.lazy(() => import('@/pages/DocumentsAndActs'));
const Finances = React.lazy(() => import('@/pages/Finances'));
const FinancesPayments = React.lazy(
  () => import('@/pages/Finances/views/Payments'),
);
const FinancesActs = React.lazy(() => import('@/pages/Finances/views/Acts'));
const Appeals = React.lazy(() => import('@/pages/Appeals'));
const AppealsList = React.lazy(() => import('@/pages/Appeals/views/AppealsList'));
const AppealsApplications = React.lazy(
  () => import('@/pages/Appeals/views/Applications'),
);

const Audit = React.lazy(() => import('@/pages/Audit'));
const NotAuthorized = React.lazy(() => import('@/pages/NotAuthorized'));

const guarded = (
  element: React.ReactElement,
  permissions: UserPermissionType[],
  allowPotentialClient = true,
) => (
  <RequireRoutePermission
    permissions={permissions}
    allowPotentialClient={allowPotentialClient}
  >
    {element}
  </RequireRoutePermission>
);

const notFoundRoute = {
  path: '*',
  element: <NotFound />,
};

export const routes: RouteObject[] = [
  {
    path: Pages.ROOT,
    element: guarded(<Home />, [UserPermissions.DashboardRead]),
  },
  {
    path: Pages.MY_COMPANY,
    element: guarded(<MyCompany />, [UserPermissions.OrganizationRead]),
  },
  {
    path: Pages.COUNTERPARTY_USERS,
    element: guarded(<CabinetUsers />, [UserPermissions.UsersRead], false),
  },
  {
    path: Pages.CONTRACTS,
    element: guarded(<Contracts />, [UserPermissions.ContractsRead]),
  },
  {
    path: Pages.CONTRACTS + '/:id',
    element: guarded(<ContractDetails />, [UserPermissions.ContractsRead]),
    children: [
      { index: true, element: guarded(<ContractActs />, [UserPermissions.ActsRead]) },
      {
        path: 'planned-volumes',
        element: guarded(<ContractPlannedVolumes />, [UserPermissions.PlannedVolumesRead]),
      },
    ],
  },
  {
    path: Pages.METERING_POINTS,
    element: guarded(<MeteringPoints />, [UserPermissions.MeteringPointsRead]),
  },
  {
    path: Pages.CONSUMPTION,
    element: guarded(<Consumption />, [UserPermissions.ConsumptionRead]),
  },
  {
    path: Pages.DOCUMENTS_AND_ACTS,
    element: guarded(<DocumentsAndActs />, [UserPermissions.ActsRead]),
  },
  {
    path: Pages.FINANCES,
    element: guarded(<Finances />, [
      UserPermissions.AccrualsRead,
      UserPermissions.DebtsRead,
      UserPermissions.PaymentsRead,
      UserPermissions.ActsRead,
    ]),
    children: [
      { index: true, element: guarded(<FinancesPayments />, [UserPermissions.PaymentsRead]) },
      { path: 'acts', element: guarded(<FinancesActs />, [UserPermissions.ActsRead]) },
    ],
  },
  {
    path: Pages.APPEALS,
    element: guarded(<Appeals />, [
      UserPermissions.AppealsRead,
      UserPermissions.ConnectionApplicationsRead,
    ]),
    children: [
      { index: true, element: guarded(<AppealsList />, [UserPermissions.AppealsRead]) },
      { path: ':id', element: guarded(<AppealsList />, [UserPermissions.AppealsRead]) },
      {
        path: 'applications',
        element: guarded(<AppealsApplications />, [UserPermissions.ConnectionApplicationsRead]),
      },
      {
        path: 'applications/:id',
        element: guarded(<AppealsApplications />, [UserPermissions.ConnectionApplicationsRead]),
      },
    ],
  },
  {
    path: Pages.AUDIT,
    element: guarded(<Audit />, [UserPermissions.AuditRead], false),
  },
  notFoundRoute,
];

export const getNestedRoutes = (routes: RouteObject[]) => {
  return routes.concat(notFoundRoute).map(({ children, ...route }, index) => (
    // @ts-ignore
    <Route key={index} {...route}>
      {children?.length ? getNestedRoutes(children) : null}
    </Route>
  ));
};

export const getRoutes = () =>
  createBrowserRouter(
    [
      // Page for oidc authorization flow
      {
        path: Pages.AUTH_CALLBACK,
        element: <AuthCallback />,
      },
      {
        path: Pages.NOT_AUTHORIZED,
        element: <NotAuthorized />,
      },
      {
        path: '*',
        element: <Router />,
      },
    ].map(route => ({ ...route, errorElement: <Error /> })),
  );
